// frontend/src/pages/dashboard/TaskComponents/TaskFormDialog.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  ListItemIcon,
  ListItemAvatar,
  IconButton,
  Divider,
  Typography,
  Box,
  Chip,
  Button,
  FormHelperText,
  useTheme,
  useMediaQuery,
  OutlinedInput,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { convertLocalToUTCStrict, convertToCompanyTime } from "../../../utils/taskHelpers";
import { getAvatarUrl } from "../../../utils/imageHelpers";
import { validateFile, ACCEPTED_MIME_TYPES } from '../../../utils/boardHelpers';
import { toast } from "react-toastify";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 350, // Increased width for avatars
    },
  },
};

const TaskFormDialog = ({
  open,
  onClose,
  dialogType,
  canEditTask,
  formData,
  setFormData,
  formErrors,
  projects,
  users,
  taskStatuses,
  sprints,
  attachments,
  files,
  setFiles,
  isSaving,
  onSubmit,
  authUser,
  hasPermission,
  currentTask,
  company,
  timerHandlers,
  fetchAttachments,
  lockedFields,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [localFiles, setLocalFiles] = useState([]);
  const [imageErrors, setImageErrors] = useState({});

  // 👇 Get selected project
  const selectedProject = useMemo(() => {
    if (!formData.project_id) return null;
    return projects.find((p) => String(p.id) === String(formData.project_id)) || null;
  }, [formData.project_id, projects]);

  // 👇 Get project-assigned users (STRICT FILTERING)
  const projectAssignedUsers = useMemo(() => {
    if (!selectedProject) return [];

    // Get assigned users from project
    const assignedUsers = selectedProject.assignedUsers || [];

    // Ensure we have valid user objects
    return assignedUsers.filter((user) => user && user.id);
  }, [selectedProject]);

  const projectAssignedUserIds = useMemo(() => {
    return projectAssignedUsers.map((u) => u.id);
  }, [projectAssignedUsers]);

  // 👇 STRICT FILTER: Show ONLY project-assigned users when project is selected
  const availableUsers = useMemo(() => {
    // If no project selected, show empty or all users (for flexibility)
    if (!formData.project_id) {
      return []; // Show empty - user must select project first
    }

    // If project has assigned users, show ONLY those users
    if (projectAssignedUserIds.length > 0) {
      return users.filter((u) => projectAssignedUserIds.includes(u.id));
    }

    // If project has NO assigned users, show empty list with message
    return [];
  }, [users, formData.project_id, projectAssignedUserIds]);

  // 👇 Auto-select project users when project changes (for new tasks)
  useEffect(() => {
    if (dialogType === "add" && formData.project_id && projectAssignedUserIds.length > 0) {
      // Always include authUser + project assigned users
      const mergedUsers = [...new Set([authUser?.id, ...projectAssignedUserIds])];
      setFormData((prev) => ({
        ...prev,
        assigned_user_ids: mergedUsers.filter(Boolean),
      }));
    } else if (dialogType === "add" && formData.project_id && projectAssignedUserIds.length === 0) {
      // If no project users, just select authUser
      setFormData((prev) => ({
        ...prev,
        assigned_user_ids: authUser?.id ? [authUser.id] : [],
      }));
    }
  }, [formData.project_id, projectAssignedUserIds, dialogType, authUser?.id, setFormData]);

  // 👇 Handle file selection with validation
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ name: file.name, reason: validation.reason });
      }
    });

    if (invalidFiles.length > 0) {
      invalidFiles.forEach(f => {
        toast.error(`❌ ${f.name}: ${f.reason}`);
      });
    }

    // ✅ Fix: Use validFiles instead of undefined selectedFiles
    setLocalFiles((prev) => [...prev, ...validFiles]);
    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  // 👇 Remove file from selection
  const handleRemoveFile = (fileName) => {
    setLocalFiles((prev) => prev.filter((f) => f.name !== fileName));
    setFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  // 👇 Handle user selection
  const handleUserChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, assigned_user_ids: value }));
  };

  // 👇 Get user name helper
  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user?.name || `User ${userId}`;
  };

  // 👇 Get user by ID
  const getUserById = (userId) => {
    return users.find((u) => u.id === userId);
  };

  // 👇 Handle image load error
  const handleImageError = (userId) => {
    setImageErrors((prev) => ({ ...prev, [userId]: true }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 2 },
          height: { xs: "100%", sm: "auto" },
          maxHeight: { xs: "100%", sm: "90vh" },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      {/* 👇 Mobile Header */}
      {isMobile && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
            zIndex: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {dialogType === "add" ? "Add New Task" : "Edit Task"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {!isMobile && (
        <DialogTitle sx={{ pb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" fontWeight={600}>
            {dialogType === "add" ? "Add New Task" : "Edit Task"}
          </Typography>
          {selectedProject && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Project: <strong>{selectedProject.name}</strong> • {projectAssignedUsers.length} team member
              {projectAssignedUsers.length !== 1 ? "s" : ""} available
            </Typography>
          )}
        </DialogTitle>
      )}

      <form onSubmit={onSubmit}>
        <DialogContent
          sx={{
            pt: isMobile ? 2 : 3,
            pb: 2,
            px: { xs: 2, sm: 3 },
            maxHeight: { xs: "calc(100vh - 120px)", sm: "60vh" },
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: theme.palette.grey[300],
              borderRadius: 3,
            },
          }}
        >
          {/* 👇 Alert if no project selected */}
          {!formData.project_id && dialogType === "add" && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, fontSize: "0.875rem" }}>
              Select a project first to see available team members
            </Alert>
          )}

          {/* 👇 Alert if project has no assigned users */}
          {formData.project_id && projectAssignedUsers.length === 0 && dialogType === "add" && (
            <Alert severity="warning" icon={<PeopleIcon />} sx={{ mb: 2, fontSize: "0.875rem" }}>
              This project has no assigned team members. You will be assigned as the task creator.
            </Alert>
          )}

          {/* 👇 Title & Description - Full Width */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 12 }}>
              <TextField
                autoFocus={!isMobile}
                name="title"
                label="Task Title *"
                fullWidth
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                disabled={dialogType === "edit" && !canEditTask}
                error={!!formErrors?.title}
                helperText={formErrors?.title}
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={dialogType === "edit" && !canEditTask}
                error={!!formErrors?.description}
                helperText={formErrors?.description}
              />
            </Grid>
          </Grid>

          {/* 👇 Project & Assignees */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                disabled={dialogType === "edit" && !canEditTask || lockedFields.project_id}
                error={!!formErrors?.project_id}
              >
                <InputLabel shrink>Project *</InputLabel>
                <Select
                  name="project_id"
                  value={formData.project_id || ""}
                  onChange={(e) => {
                    const projectId = e.target.value;
                    setFormData({
                      ...formData,
                      project_id: projectId,
                      sprint_id: "",
                    });
                  }}
                  required
                  label="Project *"
                >
                  <MenuItem value="">
                    <em>Select Project</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors?.project_id && (
                  <FormHelperText>{formErrors.project_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                disabled={
                  dialogType === "edit" && !canEditTask ||
                  lockedFields.assigned_user_ids ||
                  !formData.project_id
                }
                error={!!formErrors?.assigned_user_ids}
              >
                <InputLabel shrink>
                  Team Members {formData.project_id && `(Project Only)`}
                </InputLabel>
                <Select
                  multiple
                  name="assigned_user_ids"
                  value={formData.assigned_user_ids || []}
                  onChange={handleUserChange}
                  input={
                    <OutlinedInput
                      label={`Team Members ${formData.project_id ? "(Project Only)" : ""}`}
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const user = getUserById(id);
                        const hasImageError = imageErrors[id];
                        const avatarUrl = getAvatarUrl(user?.avatar);

                        return (
                          <Chip
                            key={id}
                            avatar={
                              <Avatar
                                alt={user?.name || "User"}
                                src={hasImageError ? null : avatarUrl}
                                onError={() => handleImageError(id)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: "0.7rem",
                                  bgcolor: hasImageError ? theme.palette.primary.main : undefined
                                }}
                              >
                                {hasImageError && user?.name?.charAt(0).toUpperCase()}
                              </Avatar>
                            }
                            label={getUserName(id)}
                            size="small"
                            onDelete={
                              dialogType === "edit" && !canEditTask
                                ? undefined
                                : () => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    assigned_user_ids: prev.assigned_user_ids.filter(
                                      (uid) => uid !== id
                                    ),
                                  }));
                                }
                            }
                            deleteIcon={
                              dialogType === "edit" && !canEditTask ? undefined : <CloseIcon />
                            }
                            sx={{
                              maxWidth: 200,
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }}
                          />
                        );
                      })}
                      {selected.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {formData.project_id
                            ? "No members selected"
                            : "Select project first"}
                        </Typography>
                      )}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  disabled={!formData.project_id}
                >
                  {/* 👇 Show message if no project selected */}
                  {!formData.project_id && (
                    <MenuItem disabled>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, px: 2 }}>
                        <InfoIcon color="warning" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Please select a project first
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}

                  {/* 👇 Show message if project has no assigned users */}
                  {formData.project_id && availableUsers.length === 0 && (
                    <MenuItem disabled>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, px: 2 }}>
                        <PeopleIcon color="error" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          No team members assigned to this project
                        </Typography>
                      </Box>
                    </MenuItem>
                  )}

                  <Divider />

                  {/* 👇 List ONLY project-assigned users with avatars */}
                  {availableUsers.map((user) => {
                    const projectCreatorId = selectedProject?.created_by;
                    const isProjectCreator = user.id === projectCreatorId;
                    const isTaskCreator =
                      dialogType === "add"
                        ? user.id === authUser?.id
                        : currentTask?.created_by === user.id;
                    const isAlreadyAssigned = formData.assigned_user_ids?.includes(user.id);
                    const hasImageError = imageErrors[user.id];
                    const avatarUrl = getAvatarUrl(user?.avatar);

                    return (
                      <MenuItem key={user.id} value={user.id} dense sx={{ py: 1, px: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 48 }}>
                          <Checkbox checked={isAlreadyAssigned} size="small" />
                        </ListItemIcon>
                        <ListItemAvatar sx={{ minWidth: 48 }}>
                          <Avatar
                            alt={user.name}
                            src={hasImageError ? null : avatarUrl}
                            onError={() => handleImageError(user.id)}
                            sx={{
                              width: 36,
                              height: 36,
                              fontSize: "0.875rem",
                              bgcolor: hasImageError ? theme.palette.primary.main : undefined
                            }}
                          >
                            {hasImageError && user.name?.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography variant="body2" fontWeight={isAlreadyAssigned ? 600 : 400}>
                                {user.name}
                              </Typography>
                              {isProjectCreator && (
                                <Chip
                                  label="Creator"
                                  size="xs"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: "0.6rem", minWidth: "auto", px: 0.5 }}
                                />
                              )}
                              {isTaskCreator && dialogType === "edit" && (
                                <Chip
                                  label="Task Creator"
                                  size="xs"
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: "0.6rem", minWidth: "auto", px: 0.5 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          }
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                          }}
                          secondaryTypographyProps={{ fontSize: "0.75rem" }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
                {formErrors?.assigned_user_ids && (
                  <FormHelperText>{formErrors.assigned_user_ids}</FormHelperText>
                )}
                {formData.project_id && (
                  <FormHelperText sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                    {availableUsers.length} project member{availableUsers.length !== 1 ? "s" : ""}{" "}
                    available
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* 👇 Sprint & Story Points */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                disabled={
                  lockedFields.sprint_id ||
                  (dialogType === "edit" && !canEditTask) ||
                  !formData.project_id
                }
              >
                <InputLabel shrink>Sprint</InputLabel>
                <Select
                  name="sprint_id"
                  value={formData.sprint_id || ""}
                  onChange={(e) => setFormData({ ...formData, sprint_id: e.target.value })}
                  label="Sprint"
                >
                  <MenuItem value="">
                    <em>No Sprint</em>
                  </MenuItem>
                  {sprints.length === 0 && formData.project_id && (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No sprints found
                      </Typography>
                    </MenuItem>
                  )}
                  {sprints.map((sprint) => (
                    <MenuItem key={sprint.id} value={sprint.id}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <span>{sprint.name}</span>
                        <Chip
                          label={sprint.status}
                          size="xs"
                          color={sprint.status === "active" ? "success" : "default"}
                          sx={{ height: 16, fontSize: "0.6rem", minWidth: "auto", px: 0.5 }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {!formData.project_id && (
                  <FormHelperText>Select a project first</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                margin="dense"
                label="Story Points"
                name="story_points"
                type="number"
                inputProps={{ min: 0, step: 0.5 }}
                value={formData.story_points || ""}
                onChange={(e) => setFormData({ ...formData, story_points: e.target.value })}
                disabled={dialogType === "edit" && !canEditTask}
              />
            </Grid>
          </Grid>

          {/* 👇 Due Date & Status */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                margin="dense"
                name="due_date"
                label="Due Date & Time"
                type="datetime-local"
                value={formData.due_date || ""}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  sx: {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    py: { xs: 1.2, sm: 1.5 },
                  },
                }}
                disabled={dialogType === "edit" && !canEditTask}
                error={!!formErrors?.due_date}
                helperText={formErrors?.due_date}
              />
            </Grid>

            <Grid item size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth disabled={dialogType === "edit" && !canEditTask}>
                <InputLabel shrink>Status *</InputLabel>
                <Select
                  name="status_id"
                  value={formData.status_id || ""}
                  onChange={(e) => setFormData({ ...formData, status_id: e.target.value })}
                  required
                  label="Status *"
                  error={!!formErrors?.status_id}
                >
                  {taskStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: status.color || "#9CA3AF",
                          }}
                        />
                        {status.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 👇 Priority - Full Width */}
          <FormControl
            fullWidth
            margin="dense"
            disabled={dialogType === "edit" && !canEditTask}
            sx={{ mb: 2 }}
          >
            <InputLabel shrink>Priority</InputLabel>
            <Select
              name="priority"
              value={formData.priority || "medium"}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              label="Priority"
            >
              <MenuItem value="low">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "success.main" }} />
                  Low
                </Box>
              </MenuItem>
              <MenuItem value="medium">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "warning.main" }} />
                  Medium
                </Box>
              </MenuItem>
              <MenuItem value="high">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "error.main" }} />
                  High
                </Box>
              </MenuItem>
              <MenuItem value="urgent">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "error.dark" }} />
                  Urgent
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* 👇 Attachments Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Attachments
            </Typography>

            <Box
              sx={{
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                cursor: dialogType === "edit" && !canEditTask ? "not-allowed" : "pointer",
                bgcolor: "action.hover",
                "&:hover": {
                  bgcolor: dialogType === "edit" && !canEditTask ? "action.hover" : "action.selected",
                },
                transition: "all 0.2s",
              }}
              onClick={() => document.getElementById("file-upload")?.click()}
            >
              <input
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={dialogType === "edit" && !canEditTask}
                style={{ display: "none" }}
                accept={ACCEPTED_MIME_TYPES}
              />
              <Typography variant="body2" color="text.secondary">
                {dialogType === "edit" && !canEditTask
                  ? "Attachments disabled"
                  : "Click to upload files (max 10MB each)"}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Images, PDF, Docs supported
              </Typography>
            </Box>

            {localFiles.length > 0 && (
              <List dense sx={{ mt: 1, maxHeight: 120, overflowY: "auto" }}>
                {localFiles.map((file, idx) => (
                  <ListItem
                    key={`${file.name}-${idx}`}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveFile(file.name)}
                        disabled={dialogType === "edit" && !canEditTask}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={<Typography variant="body2" noWrap>{file.name}</Typography>}
                      secondary={`${(file.size / 1024).toFixed(1)} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {dialogType === "edit" && attachments?.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Existing attachments:
                </Typography>
                <List dense sx={{ mt: 0.5, maxHeight: 150, overflowY: "auto" }}>
                  {attachments.map((att) => (
                    <ListItem
                      key={att.id}
                      secondaryAction={
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => timerHandlers.handleDownload(att)}
                            sx={{ mr: 0.5 }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={!canEditTask}
                            onClick={() => {
                              if (window.confirm("Delete this attachment?")) {
                                timerHandlers.handleDeleteAttachment(att.id, currentTask?.id);
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      sx={{ py: 0.5 }}
                    >
                      <ListItemText
                        primary={<Typography variant="body2" noWrap>{att.original_name}</Typography>}
                        secondary={`${(att.size / 1024).toFixed(1)} KB • ${att.file_type || "File"}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>

          {/* 👇 Time Tracking Section */}
          {dialogType === "edit" && currentTask && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  ⏱️ Time Tracking
                </Typography>
                {currentTask.tracked_time?.formatted && (
                  <Chip
                    label={currentTask.tracked_time.formatted}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                {currentTask.tracked_time?.end_time ? (
                  <Chip
                    icon={<StopIcon />}
                    label={`Ended: ${convertToCompanyTime(
                      currentTask.tracked_time.end_time,
                      company?.timezone
                    )}`}
                    color="default"
                    size="small"
                  />
                ) : timerHandlers.currentTimeEntryId ? (
                  timerHandlers.timeEntryOwner === authUser?.id ? (
                    <>
                      {!timerHandlers.isTracking ? (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<PlayArrowIcon />}
                          onClick={timerHandlers.resumeTimer}
                          disabled={isSaving}
                          size="small"
                        >
                          Resume
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<PauseIcon />}
                            onClick={timerHandlers.pauseTimer}
                            disabled={isSaving}
                            size="small"
                          >
                            Pause
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon />}
                            onClick={timerHandlers.stopTimer}
                            disabled={isSaving}
                            size="small"
                          >
                            Stop
                          </Button>
                        </>
                      )}
                      {timerHandlers.isTracking && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "success.main",
                              animation: "pulse 1.5s infinite",
                            }}
                          />
                          <Typography variant="caption" color="success.main" fontWeight={500}>
                            Live
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Chip
                      icon={<PlayArrowIcon />}
                      label={`Running by ${timerHandlers.ownerName || "user"}`}
                      size="small"
                      color="info"
                    />
                  )
                ) : (currentTask.assignedUsers?.some((u) => u.id === authUser?.id) ||
                  hasPermission("task.update") ||
                  hasPermission("task.manage.team")) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => timerHandlers.startTimer(currentTask)}
                    disabled={isSaving}
                    size="small"
                  >
                    Start Timer
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        {/* 👇 Dialog Actions */}
        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            position: isMobile ? "sticky" : "static",
            bottom: 0,
            bgcolor: "background.paper",
            borderTop: `1px solid ${theme.palette.divider}`,
            justifyContent: "space-between",
          }}
        >
          <Button onClick={onClose} disabled={isSaving} size={isMobile ? "medium" : "large"}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={(dialogType === "edit" && !canEditTask) || isSaving}
            sx={{
              minWidth: 120,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              background: `linear-gradient(135deg, #1976d2 0%, #1565c0 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, #1565c0 0%, #1976d2 100%)`,
              },
              "&:disabled": {
                background: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
            size={isMobile ? "medium" : "large"}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSaving
              ? dialogType === "add"
                ? "Adding..."
                : "Updating..."
              : dialogType === "add"
                ? "Add Task"
                : "Update Task"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TaskFormDialog;